import { SEED_CAP_BANDS } from '../data/seed';
/* A starting book, written by someone who is not this app.
 *
 * Typing eleven positions across five brokers by hand is the reason a new book
 * never gets started. The way round it is not a broker connection (§1) but a
 * file: photograph the position screens, hand the pictures and the schema below
 * to whatever assistant you like, and load what comes back.
 *
 * Which puts the weight on this file. Everything here arrives second-hand, read
 * off a screenshot by something that can misread a digit and will not say so.
 * So nothing is accepted on trust: every field is checked and located by path,
 * a file that is wrong is refused rather than half-applied, and what would be
 * written is shown before it is written. The rule the whole book runs on — an
 * unknown stays unknown — is enforced here too: a missing cost is a missing
 * cost, never a zero that reads as a free position. */

import { idFromName } from '../lib/accounts';
import { newId } from '../lib/id';
import type { BookData } from '../data/db';
import type {
  AccountRecord, CashBalance, CashFlow, Currency, Holding, Perp, Snapshot, ThemeRecord,
} from '../types';

export const STARTER_FORMAT = 'position-book-starter';
export const STARTER_VERSION = 1;

const CURRENCIES: Currency[] = ['USD', 'PLN', 'EUR', 'HKD'];

export interface StarterProblem { path: string; why: string }

export interface StarterSummary {
  accounts: number;
  holdings: number;
  cash: number;
  perps: number;
  cashFlows: number;
  themes: number;
  timeline: number;
  /** Positions whose cost was not on the screenshot. Carried, never guessed. */
  withoutCost: number;
}

export interface StarterResult {
  problems: StarterProblem[];
  /** Null whenever anything is wrong: a book is loaded whole or not at all. */
  book: BookData | null;
  summary: StarterSummary;
}

/* ---------- small checkers ---------- */

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  // A quoted number is what most assistants actually emit.
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(/\s/g, '').replace(',', '.'));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/* ---------- the parse ---------- */

export function parseStarter(text: string): StarterResult {
  const problems: StarterProblem[] = [];
  const empty: StarterSummary = {
    accounts: 0, holdings: 0, cash: 0, perps: 0, cashFlows: 0, themes: 0, timeline: 0, withoutCost: 0,
  };
  const fail = (path: string, why: string): StarterResult => ({
    problems: [{ path, why }], book: null, summary: empty,
  });

  let raw: unknown;
  try { raw = JSON.parse(text); } catch (e) {
    return fail('file', `not valid JSON: ${e instanceof Error ? e.message : 'unreadable'}`);
  }
  if (!isObject(raw)) return fail('file', 'the top level must be an object');
  if (raw['format'] !== STARTER_FORMAT) {
    return fail('format', `must be "${STARTER_FORMAT}". This does not look like a starter file`);
  }

  /* ---- accounts ---- */
  const accountsIn = Array.isArray(raw['accounts']) ? raw['accounts'] : [];
  if (accountsIn.length === 0) problems.push({ path: 'accounts', why: 'at least one broker is needed' });

  const accounts: AccountRecord[] = [];
  const byName = new Map<string, AccountRecord>();
  accountsIn.forEach((entry, i) => {
    const path = `accounts[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    const name = str(entry['name']);
    if (!name) return problems.push({ path: `${path}.name`, why: 'missing' });
    if (byName.has(name.toLowerCase())) {
      return problems.push({ path: `${path}.name`, why: `"${name}" appears twice` });
    }
    const currency = str(entry['currency'])?.toUpperCase() as Currency | undefined;
    if (!currency || !CURRENCIES.includes(currency)) {
      return problems.push({ path: `${path}.currency`, why: `must be one of ${CURRENCIES.join(', ')}` });
    }
    const record: AccountRecord = { id: idFromName(name), name, currency, order: i };
    if (entry['taxShielded'] === true) record.taxShielded = true;
    if (entry['isFutures'] === true) record.isFutures = true;
    accounts.push(record);
    byName.set(name.toLowerCase(), record);
  });

  const accountFor = (v: unknown, path: string): AccountRecord | null => {
    const name = str(v);
    if (!name) { problems.push({ path, why: 'missing' }); return null; }
    const found = byName.get(name.toLowerCase());
    if (!found) {
      problems.push({
        path,
        why: `no broker called "${name}". Known: ${[...byName.values()].map((a) => a.name).join(', ') || 'none'}`,
      });
      return null;
    }
    return found;
  };

  /* ---- holdings ---- */
  const holdings: Holding[] = [];
  let withoutCost = 0;
  const holdingsIn = Array.isArray(raw['holdings']) ? raw['holdings'] : [];
  holdingsIn.forEach((entry, i) => {
    const path = `holdings[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    const account = accountFor(entry['account'], `${path}.account`);
    const ticker = str(entry['ticker'])?.toUpperCase();
    if (!ticker) problems.push({ path: `${path}.ticker`, why: 'missing' });
    const shares = num(entry['shares']);
    if (shares === null || shares <= 0) {
      problems.push({ path: `${path}.shares`, why: 'must be a number above zero' });
    }
    if (!account || !ticker || shares === null || shares <= 0) return;

    /* A cost that was not on the screen stays off the record. Zero is this
       book's word for "not known", and the return calculation skips those lots
       rather than reading them as bought for nothing. */
    const cost = entry['avgCost'] === undefined || entry['avgCost'] === null
      ? 0
      : num(entry['avgCost']);
    if (cost === null || cost < 0) {
      return problems.push({ path: `${path}.avgCost`, why: 'not a number. Leave it out if unknown' });
    }
    if (cost === 0) withoutCost++;

    const currency = (str(entry['currency'])?.toUpperCase() as Currency | undefined) ?? account.currency;
    if (!CURRENCIES.includes(currency)) {
      return problems.push({ path: `${path}.currency`, why: `must be one of ${CURRENCIES.join(', ')}` });
    }
    const openedAt = str(entry['openedAt']);
    if (openedAt && !ISO_DATE.test(openedAt)) {
      return problems.push({ path: `${path}.openedAt`, why: 'must be YYYY-MM-DD' });
    }

    holdings.push({
      id: newId('hold'),
      ticker,
      // §3.2: the exact fetch symbol. Given one, it is used; otherwise the
      // ticker stands in until a quote confirms it.
      yahooSymbol: str(entry['yahooSymbol'])?.toUpperCase() ?? ticker,
      account: account.id,
      shares,
      avgCost: cost,
      currency,
      openedAt: openedAt ?? new Date().toISOString().slice(0, 10),
    });
  });

  /* ---- cash ---- */
  const cash: CashBalance[] = [];
  const cashIn = Array.isArray(raw['cash']) ? raw['cash'] : [];
  cashIn.forEach((entry, i) => {
    const path = `cash[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    const account = accountFor(entry['account'], `${path}.account`);
    const amount = num(entry['amount']);
    if (amount === null) problems.push({ path: `${path}.amount`, why: 'not a number' });
    if (!account || amount === null) return;
    const currency = (str(entry['currency'])?.toUpperCase() as Currency | undefined) ?? account.currency;
    cash.push({ account: account.id, amount, currency });
  });

  /* ---- perpetuals ---- */
  const perps: Perp[] = [];
  const perpsIn = Array.isArray(raw['perps']) ? raw['perps'] : [];
  perpsIn.forEach((entry, i) => {
    const path = `perps[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    const symbol = str(entry['symbol'])?.toUpperCase();
    if (!symbol) return problems.push({ path: `${path}.symbol`, why: 'missing' });
    const side = str(entry['side'])?.toLowerCase();
    if (side !== 'long' && side !== 'short') {
      return problems.push({ path: `${path}.side`, why: 'must be "long" or "short"' });
    }
    const leverage = num(entry['leverage']);
    const marginUsdt = num(entry['marginUsdt']);
    const entryPrice = num(entry['entryPrice']);
    const liqPrice = num(entry['liqPrice']);
    for (const [key, value] of [['leverage', leverage], ['marginUsdt', marginUsdt],
      ['entryPrice', entryPrice], ['liqPrice', liqPrice]] as const) {
      if (value === null || value <= 0) problems.push({ path: `${path}.${key}`, why: 'must be a number above zero' });
    }
    if (leverage === null || marginUsdt === null || entryPrice === null || liqPrice === null) return;
    if (leverage <= 0 || marginUsdt <= 0 || entryPrice <= 0 || liqPrice <= 0) return;

    const perp: Perp = {
      id: newId('perp'),
      symbol,
      display: str(entry['display'])?.toUpperCase() ?? symbol.replace(/STOCK_/, '_'),
      side,
      leverage,
      /* Notional at entry, derived rather than asked for. The figure a MEXC card
         calls "Size (USDT)" is contracts times the current mark and drifts every
         tick; feeding that to the §5.1 formula overstates the result by exactly
         how far the position has already moved. Margin times leverage cannot be
         got wrong by reading the wrong box. */
      sizeUsdt: marginUsdt * leverage,
      marginUsdt,
      entryPrice,
      liqPrice,
    };
    const tp = num(entry['takeProfit']);
    if (tp !== null && tp > 0) perp.takeProfit = tp;
    perps.push(perp);
  });

  /* ---- deposits and withdrawals ---- */
  const cashflows: CashFlow[] = [];
  const flowsIn = Array.isArray(raw['cashFlows']) ? raw['cashFlows'] : [];
  flowsIn.forEach((entry, i) => {
    const path = `cashFlows[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    /* The account is optional here, as it is in the app: money paid in counts
       against the whole book and nothing sums it per broker. A file that names
       no account for a deposit is describing one that arrived across several,
       which is what a starting figure usually is. */
    const named = entry['account'] !== undefined && entry['account'] !== null && entry['account'] !== '';
    const account = named ? accountFor(entry['account'], `${path}.account`) : null;
    const amount = num(entry['amount']);
    const date = str(entry['date']);
    const currency = str(entry['currency'])?.toUpperCase();
    if (amount === null || amount === 0) problems.push({ path: `${path}.amount`, why: 'must be a non-zero number' });
    if (!date || !ISO_DATE.test(date)) problems.push({ path: `${path}.date`, why: 'must be YYYY-MM-DD' });
    /* With no account there is no currency to fall back on, and picking one
       would be inventing the denomination of somebody's money. */
    if (!named && !currency) {
      problems.push({ path: `${path}.currency`, why: 'needed when no account is named' });
    }
    if (named && !account) return;
    if (amount === null || amount === 0 || !date || !ISO_DATE.test(date)) return;
    if (!named && !currency) return;
    const flow: CashFlow = {
      id: newId('flow'),
      amount,
      currency: currency ?? account!.currency,
      at: `${date}T12:00:00.000Z`,
    };
    if (account) flow.account = account.id;
    // The rate on the day is fetched when the app has one; it is not invented here.
    if (flow.currency === 'USD') flow.rateToUsd = 1;
    cashflows.push(flow);
  });

  /* ---- themes ---- */
  const themes: ThemeRecord[] = [];
  const themesIn = Array.isArray(raw['themes']) ? raw['themes'] : [];
  themesIn.forEach((entry, i) => {
    const path = `themes[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    const name = str(entry['name']);
    if (!name) return problems.push({ path: `${path}.name`, why: 'missing' });
    const tickers = Array.isArray(entry['tickers'])
      ? entry['tickers'].map((t) => str(t)?.toUpperCase()).filter((t): t is string => !!t)
      : [];
    themes.push({ name, tickers });
  });

  /* ---- the value line ---- */
  const snapshots: Snapshot[] = [];
  const timelineIn = Array.isArray(raw['valueTimeline']) ? raw['valueTimeline'] : [];
  timelineIn.forEach((entry, i) => {
    const path = `valueTimeline[${i}]`;
    if (!isObject(entry)) return problems.push({ path, why: 'not an object' });
    const date = str(entry['date']);
    const total = num(entry['totalUsd']);
    if (!date || !ISO_DATE.test(date)) problems.push({ path: `${path}.date`, why: 'must be YYYY-MM-DD' });
    if (total === null || total <= 0) problems.push({ path: `${path}.totalUsd`, why: 'must be a number above zero' });
    if (!date || !ISO_DATE.test(date) || total === null || total <= 0) return;
    snapshots.push({ at: `${date}T12:00:00.000Z`, totalUsd: total, contributedUsd: 0, seeded: true });
  });

  const summary: StarterSummary = {
    accounts: accounts.length,
    holdings: holdings.length,
    cash: cash.length,
    perps: perps.length,
    cashFlows: cashflows.length,
    themes: themes.length,
    timeline: snapshots.length,
    withoutCost,
  };

  if (holdings.length === 0 && perps.length === 0) {
    problems.push({ path: 'holdings', why: 'nothing to load: no positions and no contracts' });
  }

  // Whole or not at all: a book half-written from a file that was partly wrong
  // is worse than no book, because it looks finished.
  if (problems.length > 0) return { problems, book: null, summary };

  return {
    problems,
    summary,
    book: {
      accounts, holdings, cash, perps, cashflows, themes, snapshots,
      theses: [], trades: [], caps: [], dividends: [], watchlist: [], importBatches: [], alertLog: [], transfers: [], capBands: SEED_CAP_BANDS,
      conversions: [],
    },
  };
}
